
import { useState, useEffect } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

export function useAuth() {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Récupérer la session actuelle
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Écouter les changements d'authentification
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        // Traduire les erreurs courantes en français
        let errorMessage = error.message
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Email ou mot de passe incorrect'
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Veuillez confirmer votre email avant de vous connecter'
        } else if (error.message.includes('Too many requests')) {
          errorMessage = 'Trop de tentatives de connexion. Veuillez réessayer plus tard'
        }
        return { data: null, error: { message: errorMessage } }
      }
      
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: 'Erreur de connexion' } }
    }
  }

  const signUp = async (email: string, password: string, firstName: string, lastName: string, role: 'client' | 'coach' = 'client') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/auth-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          role
        })
      })

      const result = await response.json()
      
      if (!response.ok) {
        let errorMessage = result.error || 'Erreur lors de l\'inscription'
        
        // Traduire les erreurs courantes
        if (errorMessage.includes('already registered')) {
          errorMessage = 'Cette adresse email est déjà utilisée'
        } else if (errorMessage.includes('Password should be')) {
          errorMessage = 'Le mot de passe doit contenir au moins 6 caractères'
        } else if (errorMessage.includes('Invalid email')) {
          errorMessage = 'Adresse email invalide'
        }
        
        return { data: null, error: { message: errorMessage } }
      }

      // Connecter l'utilisateur après l'inscription
      return await signIn(email, password)
    } catch (error) {
      return { data: null, error: { message: 'Erreur lors de l\'inscription' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        return { error: { message: 'Erreur lors de la déconnexion' } }
      }
      return { error: null }
    } catch (error) {
      return { error: { message: 'Erreur lors de la déconnexion' } }
    }
  }

  const uploadAvatar = async (file: File, userId: string) => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${userId}.${fileExt}`
      const filePath = `avatars/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true })

      if (uploadError) {
        return { data: null, error: { message: 'Erreur lors de l\'upload de l\'image' } }
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      return { data: data.publicUrl, error: null }
    } catch (error) {
      return { data: null, error: { message: 'Erreur lors de l\'upload de l\'image' } }
    }
  }

  const getUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        return { data: null, error: { message: 'Erreur lors de la récupération du profil' } }
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: 'Erreur lors de la récupération du profil' } }
    }
  }

  const updateUserProfile = async (userId: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        return { data: null, error: { message: 'Erreur lors de la mise à jour du profil' } }
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: 'Erreur lors de la mise à jour du profil' } }
    }
  }

  return {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    uploadAvatar,
    getUserProfile,
    updateUserProfile
  }
}
